/**
 * Shared vocabulary for the coach profile (issue #290).
 *
 * The two option lists mirror the closed sets the DATABASE enforces
 * (`coach_profile_specialisms_value_check` and
 * `coach_profiles_highest_level_played_check`, api/src/db/schema.ts) and
 * that `routes/coachProfiles.ts` validates. Three copies of one list is two
 * too many, but the middle one is a network away; keeping the values here
 * in one module at least means the FRONTEND has a single copy, and a typo
 * shows up as a 400 in development rather than as a silently-dropped field.
 *
 * `value` is what goes on the wire. `label` is what a human reads, and the
 * two differ deliberately: `provincial-first-class` is a wire token chosen
 * to be stable, "Provincial / first-class" is English.
 */

export const SPECIALISM_OPTIONS = [
  { value: "batting", label: "Batting" },
  { value: "bowling", label: "Bowling" },
  { value: "fielding", label: "Fielding" },
  { value: "wicket-keeping", label: "Wicket-keeping" },
  { value: "fitness", label: "Fitness" },
  { value: "mental-skills", label: "Mental skills" },
];

export const LEVEL_PLAYED_OPTIONS = [
  { value: "club", label: "Club" },
  { value: "representative", label: "Representative" },
  { value: "provincial-first-class", label: "Provincial / first-class" },
  { value: "international", label: "International" },
];

/**
 * The two character caps the coach profile form enforces client-side
 * (`CoachProfileCard.jsx`'s `maxLength`s) and the coach set-up screen
 * reuses verbatim (issue #755) rather than retyping — both mirror the DB
 * CHECKs `coach_profiles_headline_length_check` /
 * `coach_profiles_about_length_check` (`api/src/db/schema.ts`), which are
 * the real enforcement; these are the client's early warning.
 */
export const HEADLINE_MAX = 120;
export const ABOUT_MAX = 1500;

/**
 * THE line, per #290 §2 and Martin's decision. It appears on EVERY surface
 * that renders a coach profile — the coach's own preview included, so the
 * coach knows exactly how their claims are framed to a player.
 *
 * It is one exported constant rather than copy typed into three components
 * on purpose: the failure mode this guards against is a fourth surface
 * shipping without it, and a named import is the thing a reviewer notices
 * missing. Do not soften it to "unverified" — the sentence names who
 * provided the information and who did not check it, and that is the whole
 * point. Adding verification is #288's decision, not a copy edit here.
 */
export const COACH_PROFILE_UNVERIFIED_NOTE =
  "Provided by the coach. We haven't verified it.";

export function specialismLabel(value) {
  return SPECIALISM_OPTIONS.find((o) => o.value === value)?.label || value;
}

export function levelPlayedLabel(value) {
  return LEVEL_PLAYED_OPTIONS.find((o) => o.value === value)?.label || value;
}

/**
 * True when the coach has actually written something. An all-null profile
 * row is a row, not a profile — every surface that reads one needs to tell
 * "they wrote nothing" apart from "they wrote this", and none of them
 * should be re-deriving the field list to find out.
 *
 * The photo counts (#478): a coach who has uploaded a face and written
 * nothing has still put something on their profile, and the player-side
 * card should show it.
 */
export function hasCoachProfileContent(profile) {
  if (!profile) return false;
  return Boolean(
    profile.photo ||
      profile.headline ||
      profile.years_coaching ||
      (profile.specialisms && profile.specialisms.length > 0) ||
      profile.highest_level_played ||
      profile.qualifications ||
      profile.teams_coached ||
      profile.region ||
      profile.about,
  );
}
